import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/react";
import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEMO_KEY = "robox_demo_token";
const Ctx = createContext(null);

export function RxAuthProvider({ children }) {
  const { isLoaded, isSignedIn, getToken, signOut } = useAuth();
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const [demoToken, setDemoToken] = useState(() => localStorage.getItem(DEMO_KEY));
  const [me, setMe] = useState(null);
  const authed = !!demoToken || !!isSignedIn;

  const token = useCallback(async () => demoToken || (isSignedIn ? await getTokenRef.current() : null), [demoToken, isSignedIn]);

  const api = useCallback(async (method, path, data) => {
    const t = await token();
    const r = await axios({ method, url: API + path, data, headers: t ? { Authorization: `Bearer ${t}` } : {} });
    return r.data;
  }, [token]);

  const clearDemo = () => { localStorage.removeItem(DEMO_KEY); setDemoToken(null); setMe(null); };

  const refreshMe = useCallback(async () => {
    if (!authed) return setMe(null);
    try { setMe(await api("get", "/me")); }
    catch (e) { if (e.response?.status === 401 && demoToken) clearDemo(); }
  }, [authed, api, demoToken]);

  useEffect(() => { refreshMe(); }, [refreshMe]);

  const loginDemo = (t, m) => { localStorage.setItem(DEMO_KEY, t); setDemoToken(t); setMe(m); };
  const logout = async () => { if (demoToken) clearDemo(); else await signOut(); };

  return (
    <Ctx.Provider value={{ ready: isLoaded || !!demoToken, authed, me, isPro: me?.plan === "pro", isBasic: me?.plan === "basic" || me?.plan === "pro", isDemo: !!demoToken,
      api, token, refreshMe, loginDemo, logout, setMe }}>
      {children}
    </Ctx.Provider>
  );
}

export const useRx = () => useContext(Ctx);

export function usePoll(path, ms = 5000, deps = []) {
  const { api, authed } = useRx();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!authed || !path) return;
    let alive = true;
    const run = () => api("get", path).then((d) => alive && (setData(d), setError(null)))
      .catch((e) => alive && setError(e.response?.data?.detail || e.message));
    run();
    const id = ms ? setInterval(run, ms) : null;
    return () => { alive = false; id && clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, ms, authed, api, ...deps]);
  return { data, error };
}
